"use client"

import { useRef, useState } from "react"
import { useMovingRail } from "@/hooks/use-moving-rail"
import { PRESS_OUTLETS, pressLogoUrl, type PressOutlet } from "@/lib/press-outlets"
import { useIsHydrated } from '@/hooks/use-is-hydrated'

/**
 * "As Featured" logo band (owner directive 07/20).
 *
 * Renders the shared outlet list (lib/press-outlets.ts) as full-COLOR official
 * logos on white — plain images, deliberately NOT links (owner ruling 07/20).
 * Logos live in the `press-logos` Supabase storage bucket (env-derived URL,
 * same pattern as the brand-logos bucket). If a logo image fails to load, the
 * tile falls back to the outlet's name in text so the band never shows a
 * broken-image glyph.
 *
 * The strip is a hand-driven, always-moving marquee (owner spec 07/20): a
 * requestAnimationFrame loop drifts the logos endlessly, the pointer can drag
 * or swipe it either direction with momentum, and the drift resumes on its own
 * a beat after you let go. Interaction pauses the drift only WHILE it happens —
 * touch/hover never leaves it dead-stopped. See the engine in FeaturedInBand.
 *
 * One component, used everywhere the band appears (footer sitewide, homepage,
 * about-us, /press) so the outlet set can never drift between surfaces.
 */

function LogoTile({ outlet }: { outlet: PressOutlet }) {
  const [failed, setFailed] = useState(false)
  const src = pressLogoUrl(outlet)
  if (failed || !src) {
    return (
      <span className="block text-center text-sm font-semibold text-gray-700">
        {outlet.name}
      </span>
    )
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element -- storage-hosted
    // SVG/PNG logos at native quality; next/image adds nothing for tiny assets
    <img
      src={src}
      alt={`${outlet.name} logo`}
      className="press-logo-image"
      loading="lazy"
      draggable={false}
      onError={() => setFailed(true)}
    />
  )
}

// One white logo card. `duplicate` copies exist only to make the marquee loop
// seamless, so they are hidden from assistive tech and taken out of tab order.
function LogoCard({
  outlet,
  duplicate = false,
  first = false,
  repeat = false,
}: {
  outlet: PressOutlet
  duplicate?: boolean
  first?: boolean
  repeat?: boolean
}) {
  return (
    // Pure visual feature — deliberately NOT a link (owner directive 07/20).
    // Each logo sits on its own white card that lifts slightly on
    // hover/keyboard-focus (scale + brightness) so the band feels alive
    // without ever pretending to be clickable. The cursor is the grab/grabbing
    // drag cursor (inherited from the track), signalling "drag me", never the
    // hand pointer of a link.
    <div
      data-rail-first={first || undefined}
      data-rail-repeat={repeat || undefined}
      tabIndex={duplicate ? -1 : 0}
      aria-hidden={duplicate || undefined}
      className="press-logo-card"
      title={`Fridge.com featured in ${outlet.name}`}
    >
      <LogoTile outlet={outlet} />
    </div>
  )
}

// ── Marquee engine tuning ────────────────────────────────────────────────
// One full pass of the logo set, in seconds (mobile runs a touch quicker for
// the shorter viewport). Drift speed is derived so it is width-independent.
const PASS_SECONDS_DESKTOP = 55
const PASS_SECONDS_MOBILE = 40
// Auto-drift resumes this long after the last interaction ends (owner: ~1–2s).
const RESUME_GRACE_MS = 1400
export function FeaturedInBand({
  heading = "As Featured In",
  className = "",
}: {
  heading?: string
  className?: string
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  // FRIDGE-311 (measured 08/17/2026): the two `duplicate` copies exist ONLY so
  // the marquee wraps seamlessly, and the marquee only moves once this effect
  // has mounted — so the server has no reason to ship them. The band sits in
  // the footer of every page: 44 duplicate cards were ~25 KB of the ~64 KB
  // footer on every document served (home, every collection, every article).
  // The first server-rendered/first-client render carries copy 1 alone (a
  // 22-logo row, ≈ 4,000 px, wider than the 1,100 px viewport, so the band is
  // full at first paint); the duplicates mount right after hydration, before
  // the drift can start (the loop is gated on the band being on screen). The
  // ResizeObserver below re-measures the copy width when they land.
  // Hydration flag — see hooks/use-is-hydrated.ts. The duplicates still mount
  // immediately after hydration, exactly as the comment above describes.
  const looped = useIsHydrated()

  useMovingRail(viewportRef, looped)

  if (PRESS_OUTLETS.length === 0) return null
  return (
    <div className={className}>
      {heading ? (
        // Section label — flanked by short hairline rules so the band reads as
        // a deliberate, branded section header (owner directive 07/20: stronger
        // presence, still tasteful). Only rendered when a heading is passed;
        // the /about-us and /press surfaces supply their own eyebrow instead.
        <div className="mb-6 flex items-center justify-center gap-3">
          <span className="h-px w-8 bg-gray-300" aria-hidden="true" />
          <p className="text-center text-[13px] font-bold uppercase tracking-[0.22em] text-gray-600">
            {heading}
          </p>
          <span className="h-px w-8 bg-gray-300" aria-hidden="true" />
        </div>
      ) : null}
      {/* Continuous single-row marquee (owner directive 07/20): the logos
          drift slowly and endlessly and can be dragged/swiped either way, the
          drift resuming on its own after a short grace. The track holds three
          identical copies of the set so folding the offset into one copy width
          loops seamlessly in both directions; only the first copy is exposed to
          a11y/tab order. The rAF engine lives in this component; the white
          stripe framing and edge fade live in globals.css (.press-marquee*). */}
      <div
        ref={viewportRef}
        className="press-marquee overflow-x-auto cursor-grab select-none max-w-[1100px] mx-auto"
      >
        <div
          ref={trackRef}
          className="press-marquee-track flex w-max items-center gap-3 sm:gap-5 py-1"
        >
          {PRESS_OUTLETS.map((outlet, i) => (
            <LogoCard key={outlet.name} outlet={outlet} first={i === 0} />
          ))}
          {/* Loop copies mount after hydration only — see `looped` above. */}
          {looped &&
            PRESS_OUTLETS.map((outlet, i) => (
              <LogoCard key={`dup1-${outlet.name}`} outlet={outlet} duplicate repeat={i === 0} />
            ))}
          {looped &&
            PRESS_OUTLETS.map((outlet) => (
              <LogoCard key={`dup2-${outlet.name}`} outlet={outlet} duplicate />
            ))}
        </div>
      </div>
    </div>
  )
}
